/**
 * @file ContactPage.jsx
 * @description A professionally designed, fully responsive Contact Us page for Marmasco Technologies.
 *              This page provides essential contact details and support information, including:
 *                - Phone numbers (WhatsApp Bot/Calls & WhatsApp Chat/Calls)
 *                - Email addresses (Technical and Billing Support)
 *                - Physical address
 *                - Support details (Availability, Reliability, Professionalism, and Technical assistance)
 * @author
 *   Masimba Maregedze
 * @version 1.5.1
 */

import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Spinner, ListGroup } from "react-bootstrap"; // Added ListGroup
import { Helmet } from "react-helmet";
import { runData } from "../functions/api/processor";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaHeadset } from "react-icons/fa"; // Added icons
import PageHeader from "../components/PageHeader";

/**
 * @function ContactPage
 * @description Renders a production-ready Contact Us page that provides comprehensive contact details and support information.
 *              Utilizes Bootstrap components for a responsive and professional layout.
 * @returns {JSX.Element} A Contact Us page component.
 */
const ContactPage = () => {
  const [seoData, setSeoData] = useState({
    title: "Loading...",
    meta_description: "",
    keywords: "",
    og_title: "",
    og_description: "",
    og_image: "",
    canonical_url: window.location.href,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await runData(
          { page: "contact" },
          "/web/pages/",
          "GET"
        );
        if (response.status === 200 && response.data.seo) {
          let seoResponse = response.data.seo;
          if (
            seoResponse.canonical_url &&
            seoResponse.canonical_url.startsWith("/")
          ) {
            let hostname = window.location.hostname;
            if (!hostname.startsWith("www.")) {
              hostname = "www." + hostname;
            }
            seoResponse.canonical_url =
              window.location.protocol +
              "//" +
              hostname +
              seoResponse.canonical_url;
          }
          setSeoData(seoResponse);
        }
      } catch (err) {
        console.error("Failed to load SEO data:", err);
        setError(err.message || "An error occurred while loading SEO data.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Container
        fluid
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "100vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container
        fluid
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "100vh" }}
      >
        <h2>Error: {error}</h2>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>{seoData.title} - Marmasco Technologies</title>
        <meta name="description" content={seoData.meta_description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta property="og:title" content={seoData.og_title} />
        <meta property="og:description" content={seoData.og_description} />
        <meta property="og:image" content={seoData.og_image} />
        <link rel="canonical" href={seoData.canonical_url} />
        <script type="application/ld+json">
          {`
            {
              "@context": "http://schema.org",
              "@type": "WebPage",
              "name": "${seoData.title} - Marmasco Technologies",
              "url": "${seoData.canonical_url}",
              "description": "${seoData.meta_description}"
            }
          `}
        </script>
      </Helmet>

      {/* Header Section */}
      <PageHeader seoData={seoData} />

      {/* Main Content Section */}
      <Container
        id="content-start"
        className="bg-light py-5 fade-in mb-4 rounded-4 shadow-lg"
        style={{
          marginTop: "-6rem",
          borderRadius: "1rem",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Section Header */}
        <Row className="mb-5">
          <Col className="text-center px-md-4 px-2">
            {/* Apply consistent heading and paragraph styling */}
            <h2 className="display-5 fw-bold mb-3 section-title">
              Get In Touch
            </h2>
            <p
              className="lead text-muted mx-auto"
              style={{ maxWidth: "800px" }}
            >
              We're here to help! Reach out to us using the contact details
              below or visit our office.
            </p>
          </Col>
        </Row>

        {/* Contact Details Row - Split into two columns */}
        <Row className="mb-4 px-md-4 px-2 justify-content-center">
          {/* Column 1: Phone & Email */}
          <Col md={6} className="d-flex align-items-stretch mb-4 mb-md-0">
            <Card className="h-100 border-0 shadow-sm rounded-3 w-100">
              {/* Add padding to Card.Body (e.g., p-4) */}
              <Card.Body className="d-flex flex-column p-4">
                {/* Remove text-center and section-title */}
                <Card.Title className="mb-4">
                  <h5>
                    <FaPhone className="me-2 text-primary" />
                    Direct Contact
                  </h5>
                </Card.Title>
                <ListGroup variant="flush" className="flex-grow-1">
                  <ListGroup.Item className="border-0 px-4"> {/* Remove borders/padding */}
                    <strong className="d-block mb-2">Phone Numbers:</strong>
                    <a href="tel:+263785833095" className="text-decoration-none d-block mb-1">+263 78 583 3095</a>
                    <a href="tel:+263714602698" className="text-decoration-none d-block">+263 71 460 2698</a>
                  </ListGroup.Item>
                  <ListGroup.Item className="border-0 px-4 mt-3"> {/* Remove borders/padding */}
                    <strong className="d-block mb-2">
                      <FaEnvelope className="me-2" />
                      Email Addresses:
                    </strong>
                    <a href="mailto:support@marmasco.com" className="text-decoration-none d-block mb-1">support@marmasco.com</a>
                    <a href="mailto:finance@marmasco.com" className="text-decoration-none d-block">finance@marmasco.com</a>
                  </ListGroup.Item>
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>

          {/* Column 2: Address & Support Info */}
          <Col md={6} className="d-flex align-items-stretch">
            <Card className="h-100 border-0 shadow-sm rounded-3 w-100">
              {/* Add padding to Card.Body (e.g., p-4) */}
              <Card.Body className="d-flex flex-column p-4">
                 {/* Remove text-center and section-title */}
                 <Card.Title className="mb-4">
                  <h5>
                    <FaMapMarkerAlt className="me-2 text-primary" />
                    Visit Us / Support
                  </h5>
                </Card.Title>
                 <ListGroup variant="flush" className="flex-grow-1">
                   <ListGroup.Item className="border-0 px-4"> {/* Remove borders/padding */}
                    <strong className="d-block mb-2">Physical Address:</strong>
                    <p className="mb-0">15992 Unit P, Seke</p>
                    <p>Chitungwiza, Zimbabwe</p>
                    {/* Optional: Add Google Maps Link */}
                    {/* <a href="YOUR_GOOGLE_MAPS_LINK" target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary mt-2">View on Map</a> */}
                  </ListGroup.Item>
                   <ListGroup.Item className="border-0 px-4 mt-3"> {/* Remove borders/padding */}
                     <strong className="d-block mb-2">
                       <FaHeadset className="me-2" />
                       Support Availability:
                     </strong>
                     <p className="text-muted mb-0">
                       Our technical and billing support teams are available 24/7 via email. Phone support is available during standard business hours (Mon-Fri, 8 AM - 5 PM CAT).
                     </p>
                   </ListGroup.Item>
                 </ListGroup>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default ContactPage;
